/**
 * profileApi — typed client for the self-service "My Profile" screen
 * (M02 §29 / M02-G02).
 *
 * Every call here is self-service: it names no userId and carries no RBAC
 * permission, because the server reads the caller's identity from the session.
 * Contact view/edit use `GET`/`PATCH /v1/users/me`; password change uses
 * `POST /v1/users/me/change-password` (AuthnService.changeOwnPassword).
 *
 * They deliberately do NOT reuse the User Master endpoints (`/v1/users/:id`):
 * those require M02_USERS_VIEW / M02_USERS_EDIT, which is the administrator's
 * right to read and edit *everybody* — asking for it here meant an ordinary
 * user could not open their own profile.
 */
import { apiRequest } from './client.js';
import type { User } from './usersApi.js';

export type { User } from './usersApi.js';

type Tok = string | null | undefined;

/** Contact fields a user may edit on their own profile (+ lock version). */
export interface UpdateContactInput {
  readonly firstName: string;
  readonly middleName?: string;
  readonly lastName: string;
  readonly displayName: string;
  readonly mobileNumber: string;
  readonly emailAddress: string;
  readonly version: number;
}

/** POST /v1/users/me/change-password body (ChangePasswordRequestDto). */
export interface ChangePasswordInput {
  readonly oldPassword: string;
  readonly newPassword: string;
}

/** GET /v1/users/me — the caller's own profile record. */
export function getMyProfile(token?: Tok): Promise<User> {
  return apiRequest<User>('/users/me', { token });
}

/** PATCH /v1/users/me — persist edited contact fields on the caller's record. */
export function updateMyContact(
  body: UpdateContactInput,
  token?: Tok,
): Promise<User> {
  return apiRequest<User>('/users/me', { method: 'PATCH', token, body });
}

/** Change the caller's own password (verifies the current password server-side). */
export function changeMyPassword(
  body: ChangePasswordInput,
  token?: Tok,
): Promise<{ changed: true }> {
  return apiRequest<{ changed: true }>('/users/me/change-password', {
    method: 'POST',
    token,
    body,
  });
}
